import React, { useEffect, useMemo, useState } from "react";
import {
  changeMyCar,
  fetchBrands,
  fetchModels,
  fetchMyCar,
  fetchProfileEdit,
  selectMyCar,
  updateMyCar,
  updateMyProfile,
} from "../Api";
import ExpandableEventCard from "../components/ExpandableEventCard";

function normalizePlateUI(s) {
  return String(s || "")
    .toUpperCase()
    .replace(/[^\dA-Z]/g, "")
    .trim();
}

function asText(err) {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (typeof err?.message === "string") return err.message;
  const d = err?.detail || err?.data?.detail || err?.data;
  if (typeof d === "string") return d;
  try {
    return JSON.stringify(d || err);
  } catch {
    return String(err);
  }
}

const COLOR_OPTIONS = [
  { value: "", label: "Seçiniz" },
  { value: "beyaz", label: "Beyaz" },
  { value: "siyah", label: "Siyah" },
  { value: "gri", label: "Gri" },
  { value: "gümüş", label: "Gümüş / Gri" },
  { value: "kırmızı", label: "Kırmızı" },
  { value: "mavi", label: "Mavi" },
  { value: "yeşil", label: "Yeşil" },
  { value: "sarı", label: "Sarı" },
  { value: "turuncu", label: "Turuncu" },
  { value: "kahverengi", label: "Kahverengi" },
  { value: "bej", label: "Bej" },
];

const YANDEX_RU_COLOR_TO_TR = {
  Желтый: "sarı",
  Белый: "beyaz",
  Черный: "siyah",
  Синий: "mavi",
  Голубой: "mavi",
  Красный: "kırmızı",
  Зеленый: "yeşil",
  Оранжевый: "turuncu",
  Серый: "gri",
  "Серебристый": "gümüş",
  Коричневый: "kahverengi",
  Бежевый: "bej",
  Фиолетовый: "mor",
  Розовый: "pembe",
};

function normalizeColorFromYandex(color) {
  const raw = String(color || "").trim();
  if (!raw) return "";
  if (YANDEX_RU_COLOR_TO_TR[raw]) return YANDEX_RU_COLOR_TO_TR[raw];
  const lower = raw.toLowerCase();
  if (COLOR_OPTIONS.some((o) => o.value === lower)) return lower;
  const ascii = lower
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ç/g, "c")
    .replace(/ö/g, "o")
    .replace(/ü/g, "u")
    .replace(/ğ/g, "g");
  const fromAscii = COLOR_OPTIONS.find((o) => o.value === ascii);
  return fromAscii ? fromAscii.value : lower;
}

function colorDisplayLabel(value) {
  const key = normalizeColorFromYandex(value);
  const opt = COLOR_OPTIONS.find((o) => o.value === key);
  return opt?.label || value || "";
}

function pickCarFields(carDetail, plateFallback) {
  const specs = carDetail?.vehicle_specifications || {};
  const licenses = carDetail?.vehicle_licenses || {};
  return {
    plate:
      licenses.licence_plate_number ||
      licenses.plate_number ||
      plateFallback ||
      "",
    brand: specs.brand || "",
    model: specs.model || "",
    color: normalizeColorFromYandex(specs.color || ""),
    year: specs.year ? String(specs.year) : "",
    vin: specs.vin || "",
    transmission: specs.transmission || "automatic",
  };
}

function carMetaLine(car) {
  if (!car) return "";
  return [car.brand, car.model, car.year, colorDisplayLabel(car.color)].filter(Boolean).join(" · ");
}

const TRANSMISSION_OPTIONS = [
  { value: "automatic", label: "Otomatik" },
  { value: "mechanical", label: "Manuel" },
  { value: "robotic", label: "Robotik" },
  { value: "variator", label: "Varyatör" },
];

function Field({ label, children }) {
  return (
    <label className="driver-field">
      <span className="driver-field-label">{label}</span>
      {children}
    </label>
  );
}

function Alert({ type, children }) {
  if (!children) return null;
  return <div className={`driver-alert driver-alert--${type}`}>{children}</div>;
}

function CarSettingsCard({ onCarChanged }) {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [meErr, setMeErr] = useState("");

  const [form, setForm] = useState({
    plate: "",
    brand: "",
    model: "",
    color: "",
    year: String(new Date().getFullYear()),
    vin: "",
    transmission: "automatic",
  });

  const [changePlate, setChangePlate] = useState("");
  const [flow, setFlow] = useState("idle"); // idle | needCreate | editing
  const [brands, setBrands] = useState([{ value: "", label: "Seçiniz" }]);
  const [models, setModels] = useState([{ value: "", label: "Seçiniz" }]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);

  const [busySave, setBusySave] = useState(false);
  const [busyChange, setBusyChange] = useState(false);
  const [busySelect, setBusySelect] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const cars = Array.isArray(me?.cars) ? me.cars : [];
  const hasCar = Boolean(me?.car_id);
  const selectedCar = cars.find((c) => c.selected) || cars.find((c) => c.car_id === me?.car_id) || null;
  const busy = busySave || busyChange || Boolean(busySelect);

  async function loadMe() {
    setLoading(true);
    setMeErr("");
    try {
      const data = await fetchMyCar();
      setMe(data || null);
      const fields = pickCarFields(data?.car_detail, data?.plate);
      setForm((f) => ({ ...f, ...fields }));
    } catch (e) {
      setMeErr(asText(e) || "Araç bilgisi alınamadı.");
      setMe(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadMe();
  }, []);

  async function loadBrandsOnce() {
    if (brands.length > 1) return;
    setBrandsLoading(true);
    try {
      const data = await fetchBrands();
      const list = (data?.brands || []).map((b) => ({
        value: b.value || b.label,
        label: b.label || b.value,
      }));
      setBrands([{ value: "", label: "Seçiniz" }, ...list]);
    } catch (e) {
      setErr(asText(e) || "Markalar yüklenemedi.");
    } finally {
      setBrandsLoading(false);
    }
  }

  async function loadModelsForBrand(brand) {
    if (!brand) {
      setModels([{ value: "", label: "Seçiniz" }]);
      return;
    }
    setModelsLoading(true);
    try {
      const data = await fetchModels(brand);
      const list = (data?.models || []).map((m) => ({
        value: m.value || m.label,
        label: m.label || m.value,
      }));
      setModels([{ value: "", label: "Seçiniz" }, ...list]);
    } catch (e) {
      setErr(asText(e) || "Modeller yüklenemedi.");
      setModels([{ value: "", label: "Seçiniz" }]);
    } finally {
      setModelsLoading(false);
    }
  }

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function openEditSelected() {
    if (!hasCar) return;
    const fields = pickCarFields(me?.car_detail, me?.plate || selectedCar?.plate);
    if (selectedCar) {
      setForm((f) => ({
        ...f,
        ...fields,
        plate: fields.plate || selectedCar.plate || "",
        brand: fields.brand || selectedCar.brand || "",
        model: fields.model || selectedCar.model || "",
        year: fields.year || selectedCar.year || f.year,
        color: fields.color || normalizeColorFromYandex(selectedCar.color) || "",
      }));
    } else {
      setForm((f) => ({ ...f, ...fields }));
    }
    setFlow("editing");
    setErr("");
    setOk("");
  }

  async function handleSelectCar(carId) {
    if (!carId || carId === me?.car_id) return;
    setErr("");
    setOk("");
    setBusySelect(carId);
    try {
      await selectMyCar(carId);
      setOk("Araç seçildi. Yandex'te aktif araç güncellendi.");
      setFlow("idle");
      await loadMe();
      onCarChanged?.();
    } catch (e) {
      setErr(asText(e) || "Araç seçilemedi.");
    } finally {
      setBusySelect("");
    }
  }

  async function handleUpdateCar(e) {
    e.preventDefault();
    setErr("");
    setOk("");
    if (!hasCar) {
      setErr("Güncellenecek bağlı araç yok. Önce araç seçin.");
      return;
    }
    if (!form.brand || !form.model || !form.color || !form.year) {
      setErr("Marka, model, renk ve yıl zorunlu.");
      return;
    }
    setBusySave(true);
    try {
      await updateMyCar({
        brand: form.brand,
        model: form.model,
        color: form.color,
        year: parseInt(form.year, 10),
        vin: form.vin || null,
        transmission: form.transmission || "automatic",
        plate: form.plate || null,
        car_id: me?.car_id || null,
      });
      setOk("Araç özellikleri güncellendi.");
      setFlow("idle");
      await loadMe();
      onCarChanged?.();
    } catch (e2) {
      setErr(asText(e2) || "Araç güncellenemedi.");
    } finally {
      setBusySave(false);
    }
  }

  async function handleAddCar(e) {
    e.preventDefault();
    setErr("");
    setOk("");
    const p = normalizePlateUI(changePlate);
    if (!p) {
      setErr("Plaka girin.");
      return;
    }
    setChangePlate(p);
    setBusyChange(true);
    try {
      await changeMyCar({ plate: p, create_if_missing: false });
      setOk(`Araç eklendi ve seçildi: ${p}`);
      setFlow("idle");
      setChangePlate("");
      await loadMe();
      onCarChanged?.();
    } catch (e2) {
      const msg = asText(e2);
      if (e2?.status === 404 || /bulunamadı/i.test(msg)) {
        setFlow("needCreate");
        setForm((f) => ({ ...f, plate: p }));
        setOk("");
        setErr("Bu plaka sistemde yok. Aşağıdan yeni araç kaydı oluşturabilirsiniz.");
        await loadBrandsOnce();
      } else {
        setErr(msg || "Araç eklenemedi.");
      }
    } finally {
      setBusyChange(false);
    }
  }

  async function handleCreateAndAssign(e) {
    e.preventDefault();
    setErr("");
    setOk("");
    const p = normalizePlateUI(form.plate || changePlate);
    if (!p || !form.brand || !form.model || !form.color || !form.year) {
      setErr("Plaka, marka, model, renk ve yıl zorunlu.");
      return;
    }
    setBusyChange(true);
    try {
      await changeMyCar({
        plate: p,
        create_if_missing: true,
        new_car: {
          plate: p,
          brand: form.brand,
          model: form.model,
          color: form.color,
          year: parseInt(form.year, 10),
          vin: form.vin || null,
        },
      });
      setOk(`Araç oluşturuldu ve seçildi: ${p}`);
      setFlow("idle");
      setChangePlate("");
      await loadMe();
      onCarChanged?.();
    } catch (e2) {
      setErr(asText(e2) || "Araç oluşturulamadı.");
    } finally {
      setBusyChange(false);
    }
  }

  const editMeta = useMemo(() => {
    const parts = [form.brand, form.model, form.year, colorDisplayLabel(form.color)].filter(Boolean);
    return parts.join(" · ");
  }, [form]);

  return (
    <div className="driver-form-card profile-settings-form" onClick={(e) => e.stopPropagation()}>
      {loading ? <div className="muted">Yükleniyor…</div> : null}
      {meErr ? <Alert type="error">{meErr}</Alert> : null}

      <div className="garage-section">
        <h4 className="profile-section-title">Araçlarım</h4>
        <p className="muted" style={{ margin: "0 0 8px" }}>
          Bir araç seçin — seçilen araç Yandex'te aktif olur. Düzenleme yalnızca seçili araçta açılır.
        </p>

        {!loading && cars.length === 0 ? (
          <div className="profile-car-summary">
            <div className="profile-car-plate">Araç yok</div>
            <div className="muted" style={{ marginTop: 6 }}>
              Listenizde araç yok. Aşağıdan plaka ile ekleyin veya oluşturun.
            </div>
          </div>
        ) : null}

        <div className="garage-list">
          {cars.map((car) => {
            const selected = Boolean(car.selected);
            const selecting = busySelect === car.car_id;
            const meta = carMetaLine(car);
            return (
              <div
                key={car.car_id}
                className={`garage-item${selected ? " garage-item--selected" : ""}`}
              >
                <button
                  type="button"
                  className="garage-item__select"
                  disabled={busy || selected}
                  onClick={() => handleSelectCar(car.car_id)}
                  title={selected ? "Seçili araç" : "Bu aracı seç"}
                >
                  <span className={`garage-radio${selected ? " garage-radio--on" : ""}`} aria-hidden />
                  <span className="garage-item__body">
                    <span className="garage-item__plate">{car.plate || "Plaka yok"}</span>
                    {meta ? <span className="garage-item__meta muted">{meta}</span> : null}
                    {selected ? <span className="garage-item__badge">Seçili · Yandex</span> : null}
                    {selecting ? <span className="muted">Seçiliyor…</span> : null}
                  </span>
                </button>
                {selected ? (
                  <button
                    type="button"
                    className="garage-item__edit"
                    disabled={busy}
                    onClick={openEditSelected}
                    title="Düzenle"
                    aria-label="Seçili aracı düzenle"
                  >
                    ✎
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {flow === "editing" && hasCar ? (
        <form onSubmit={handleUpdateCar} className="profile-form-grid" style={{ marginTop: 12 }}>
          <h4 className="profile-section-title">Araç özellikleri</h4>
          {editMeta ? <div className="muted">{editMeta}</div> : null}
          <Field label="Plaka">
            <input
              className="input"
              value={form.plate}
              onChange={(e) => setField("plate", normalizePlateUI(e.target.value))}
            />
          </Field>
          <Field label="Marka">
            <input className="input" value={form.brand} onChange={(e) => setField("brand", e.target.value)} />
          </Field>
          <Field label="Model">
            <input className="input" value={form.model} onChange={(e) => setField("model", e.target.value)} />
          </Field>
          <Field label="Yıl">
            <input
              className="input"
              type="number"
              value={form.year}
              onChange={(e) => setField("year", e.target.value)}
            />
          </Field>
          <Field label="Renk">
            <select className="input" value={form.color} onChange={(e) => setField("color", e.target.value)}>
              {COLOR_OPTIONS.map((o) => (
                <option key={o.value || "empty"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Vites">
            <select
              className="input"
              value={form.transmission}
              onChange={(e) => setField("transmission", e.target.value)}
            >
              {TRANSMISSION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="VIN">
            <input className="input" value={form.vin} onChange={(e) => setField("vin", e.target.value)} />
          </Field>
          <button className="btn" type="submit" disabled={busy}>
            {busySave ? "Kaydediliyor…" : "Özellikleri Kaydet"}
          </button>
          <button
            type="button"
            className="btn-outline"
            disabled={busy}
            onClick={() => {
              setFlow("idle");
              setErr("");
            }}
          >
            Vazgeç
          </button>
        </form>
      ) : null}

      <div className="profile-divider" />

      <form onSubmit={handleAddCar} className="profile-form-grid">
        <h4 className="profile-section-title">Araç ekle</h4>
        <p className="muted" style={{ margin: 0 }}>
          Yeni plaka ekleyin. Araç listede yoksa kayıt formu açılır; eklenen araç seçilir.
        </p>
        <Field label="Plaka">
          <input
            className="input"
            value={changePlate}
            onChange={(e) => setChangePlate(e.target.value)}
            placeholder="örn: 07T4002"
          />
        </Field>
        <button className="btn" type="submit" disabled={busy}>
          {busyChange ? "İşleniyor…" : "Araç Ekle"}
        </button>
      </form>

      {flow === "needCreate" ? (
        <form onSubmit={handleCreateAndAssign} className="profile-form-grid" style={{ marginTop: 16 }}>
          <h4 className="profile-section-title">Yeni araç kaydı</h4>
          <Field label="Plaka">
            <input
              className="input"
              value={form.plate}
              onChange={(e) => setField("plate", normalizePlateUI(e.target.value))}
            />
          </Field>
          <Field label={`Marka ${brandsLoading ? "(yükleniyor…)" : ""}`}>
            <select
              className="input"
              value={form.brand}
              disabled={brandsLoading}
              onFocus={loadBrandsOnce}
              onChange={(e) => {
                const v = e.target.value;
                setField("brand", v);
                setField("model", "");
                loadModelsForBrand(v);
              }}
            >
              {brands.map((o) => (
                <option key={o.value || "b"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label={`Model ${modelsLoading ? "(yükleniyor…)" : ""}`}>
            <select
              className="input"
              value={form.model}
              disabled={!form.brand || modelsLoading}
              onChange={(e) => setField("model", e.target.value)}
            >
              {models.map((o) => (
                <option key={o.value || "m"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Yıl">
            <input
              className="input"
              type="number"
              value={form.year}
              onChange={(e) => setField("year", e.target.value)}
            />
          </Field>
          <Field label="Renk">
            <select className="input" value={form.color} onChange={(e) => setField("color", e.target.value)}>
              {COLOR_OPTIONS.map((o) => (
                <option key={o.value || "c"} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="VIN (opsiyonel)">
            <input className="input" value={form.vin} onChange={(e) => setField("vin", e.target.value)} />
          </Field>
          <button className="btn" type="submit" disabled={busy}>
            {busyChange ? "Kaydediliyor…" : "Aracı Oluştur ve Seç"}
          </button>
          <button
            type="button"
            className="btn-outline"
            disabled={busy}
            onClick={() => {
              setFlow("idle");
              setErr("");
            }}
          >
            Vazgeç
          </button>
        </form>
      ) : null}

      <Alert type="error">{err}</Alert>
      <Alert type="ok">{ok}</Alert>
    </div>
  );
}

function ProfileSettingsCard({ onProfileSaved }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    middle_name: "",
    phone: "",
    email: "",
    address: "",
    driver_license_number: "",
    birth_date: "",
    license_issue_date: "",
    license_expiry_date: "",
    license_country: "tur",
    tax_identification_number: "",
  });

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const data = await fetchProfileEdit();
        setForm((f) => ({ ...f, ...(data?.fields || {}) }));
      } catch (e) {
        setErr(asText(e) || "Profil bilgisi alınamadı.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setOk("");
    setBusy(true);
    try {
      const resp = await updateMyProfile({
        email: form.email?.trim() || null,
        address: form.address?.trim() || null,
        birth_date: form.birth_date || null,
        license_issue_date: form.license_issue_date || null,
        license_expiry_date: form.license_expiry_date || null,
        tax_identification_number: form.tax_identification_number?.trim() || null,
      });
      if (resp?.fields) setForm((f) => ({ ...f, ...resp.fields }));
      if (resp?.profile) {
        try {
          localStorage.setItem("profile", JSON.stringify(resp.profile));
        } catch {
          /* ignore */
        }
        onProfileSaved?.(resp.profile);
      }
      setOk("Profil güncellendi.");
    } catch (e2) {
      setErr(asText(e2) || "Profil güncellenemedi.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="muted">Yükleniyor…</div>;

  return (
    <form className="driver-form-card profile-settings-form profile-form-grid" onSubmit={onSubmit} onClick={(e) => e.stopPropagation()}>
      <Field label="Ad">
        <input className="input input--readonly" value={form.first_name} readOnly />
      </Field>
      <Field label="Soyad">
        <input className="input input--readonly" value={form.last_name} readOnly />
      </Field>
      <Field label="Telefon">
        <input className="input input--readonly" value={form.phone} readOnly />
      </Field>
      <Field label="E-posta">
        <input className="input" type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} />
      </Field>
      <Field label="Adres">
        <input className="input" value={form.address} onChange={(e) => setField("address", e.target.value)} />
      </Field>
      <Field label="Ehliyet no">
        <input className="input input--readonly" value={form.driver_license_number} readOnly />
      </Field>
      <Field label="Doğum tarihi">
        <input
          className="input"
          type="date"
          value={form.birth_date}
          onChange={(e) => setField("birth_date", e.target.value)}
        />
      </Field>
      <Field label="Ehliyet veriliş">
        <input
          className="input"
          type="date"
          value={form.license_issue_date}
          onChange={(e) => setField("license_issue_date", e.target.value)}
        />
      </Field>
      <Field label="Ehliyet bitiş">
        <input
          className="input"
          type="date"
          value={form.license_expiry_date}
          onChange={(e) => setField("license_expiry_date", e.target.value)}
        />
      </Field>
      <Field label="Vergi no (VKN/TCKN)">
        <input
          className="input"
          value={form.tax_identification_number}
          onChange={(e) => setField("tax_identification_number", e.target.value)}
        />
      </Field>
      <button className="btn" type="submit" disabled={busy}>
        {busy ? "Kaydediliyor…" : "Profili Kaydet"}
      </button>
      <Alert type="error">{err}</Alert>
      <Alert type="ok">{ok}</Alert>
    </form>
  );
}

export default function CarChangePage() {
  return (
    <div className="driver-page-wrap">
      <div className="driver-page-head">
        <h1 className="driver-page-title">Profil Ayarları</h1>
        <p className="driver-page-sub">Araç ve sürücü profil bilgilerini yönetin.</p>
      </div>

      <div className="withdraw-expandable-grid">
        <ExpandableEventCard
          cardId="car-settings"
          headerVariant="car"
          title="ARAÇ AYARLARI"
          description="Birden fazla araç ekleyin, birini seçin (Yandex'te aktif olur)"
          content={<CarSettingsCard />}
        />
        <ExpandableEventCard
          cardId="profile-settings"
          headerVariant="profile"
          title="PROFİL AYARLARI"
          description="Ad, iletişim ve ehliyet bilgilerinizi güncelleyin"
          content={<ProfileSettingsCard />}
        />
      </div>
    </div>
  );
}
